import { Pin, PinComponent, PinId, TeacherPin } from '@lessonup/pins-shared';
import { cloneDeep } from 'lodash';
import { Doc as YDoc } from 'yjs';
import { bind } from '../immer-yjs';
import {
  copyTipTapFragmentToComponents,
  copyTipTapFragmentToPins,
  getDataRoot,
  moveTipTapContentToFragments,
} from './y-doc-fragment-helpers';
import { YjsLesson, YjsLessonCanonicalProperties, YjsLessonVirtualProperties } from './yjs-lesson';

export class YjsDecodeError extends Error {
  public constructor(private readonly parent: Error) {
    super(parent.message);
    this.name = 'YjsDecodeError';
  }
}

export class YjsEncodeError extends Error {
  public constructor(private readonly parent: Error) {
    super(parent.message);
    this.name = 'YjsEncodeError';
  }
}

export function decodeFromYjs<T extends YDoc>(document: T): YjsLessonCanonicalProperties {
  try {
    const binder = bind<YjsLesson>(getDataRoot(document));
    const lesson = binder.get();
    return {
      id: lesson.id,
      pins: decodeYjsPins(document, lesson.pins),
    };
  } catch (error) {
    throw new YjsDecodeError(error as Error);
  }
}

export function encodeToYjs<T extends YDoc>(
  document: T,
  lessonCanonical: YjsLessonCanonicalProperties,
  lessonVirtual: YjsLessonVirtualProperties
): T {
  try {
    const binder = bind<YjsLesson>(getDataRoot(document));
    binder.update((state) => {
      state.id = lessonCanonical.id;
      state.pins = cloneDeep(lessonCanonical.pins).map((pin: TeacherPin) => {
        moveTipTapContentToFragments(document, pin);
        return pin;
      });
      state.latestPinThemeId = lessonVirtual.latestPinThemeId;
      state.pinThemes = lessonVirtual.pinThemes;
    });
    binder.unbind();
    return document;
  } catch (error) {
    throw new YjsEncodeError(error as Error);
  }
}

export function decodeYjsPinComponents(yDoc: YDoc, pinId: PinId, pinComponents: PinComponent[]): PinComponent[] {
  const clonedPinComponents = cloneDeep(pinComponents);
  copyTipTapFragmentToComponents(yDoc, pinId, clonedPinComponents);
  return clonedPinComponents;
}

export function decodeYjsPins<T extends Pin>(yDoc: YDoc, pins: T[]): T[] {
  const clonedPins = cloneDeep(pins);
  copyTipTapFragmentToPins(yDoc, clonedPins);
  return clonedPins;
}
